import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, BRAND } from "@/lib/config";
import { useAuth } from "@/lib/auth";
import { invalidateTiersCache } from "@/lib/useTiers";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const paypalToken = params.get("token"); // PayPal order ID
  const [state, setState] = useState({ status: "polling", tries: 0, tier_key: "" });
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    // --- PayPal flow (preferred) ---
    if (paypalToken) {
      (async () => {
        try {
          const { data } = await api.post("/payments/paypal/capture", { order_id: paypalToken });
          await refresh();
          invalidateTiersCache();
          setState({ status: "paid", tier_key: data.kind === "tier" ? "tier" : "" });
        } catch (e) {
          setState({ status: "failed", message: formatApiError(e?.response?.data?.detail) || "Capture failed" });
        }
      })();
      return () => { cancelledRef.current = true; };
    }

    // --- Legacy Stripe polling (kept for safety during the switchover) ---
    if (!sessionId) {
      setState({ status: "error", message: "Missing session_id" });
      return;
    }
    let pollCount = 0;
    const poll = async () => {
      if (cancelledRef.current) return;
      pollCount += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          invalidateTiersCache();
          setState({ status: "paid", tier_key: data.tier_key });
          return;
        }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) {
          setState({ status: "failed", message: data.payment_status });
          return;
        }
      } catch (e) {
        // network hiccup — retry
      }
      if (pollCount > 25) {
        setState({ status: "timeout" });
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelledRef.current = true; };
  }, [sessionId, paypalToken, refresh]);

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {state.status === "polling" && (
          <div data-testid="payment-polling">
            <Loader2 className="w-14 h-14 mx-auto text-[#FF8A00] animate-spin" />
            <h1 className="font-display text-3xl mt-6">Confirming your payment…</h1>
            <p className="text-white/55 mt-3 text-sm">This usually takes a few seconds. Don't close this tab.</p>
          </div>
        )}
        {state.status === "paid" && (
          <div className="fade-up" data-testid="payment-success">
            <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-[#FF8A00] to-[#E52E71] flex items-center justify-center pulse-glow">
              <Sparkles size={32} />
            </div>
            <h1 className="font-display text-4xl mt-8">You're in.</h1>
            <p className="text-white/60 mt-3 max-w-sm mx-auto">
              Payment confirmed. Your new content is unlocked — step into purpose.
            </p>
            <Button data-testid="go-dashboard"
              onClick={() => navigate("/dashboard")}
              className="mt-8 rounded-full px-10 py-6 bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
              Enter the lab
            </Button>
          </div>
        )}
        {state.status === "failed" && (
          <div data-testid="payment-failed">
            <XCircle className="w-16 h-16 mx-auto text-[#E52E71]" />
            <h1 className="font-display text-3xl mt-6">Payment {state.message}</h1>
            <p className="text-white/55 mt-3 text-sm">No charge was made. You can try again anytime.</p>
            <Button onClick={() => navigate("/pricing")} className="mt-6 rounded-full bg-white/10 hover:bg-white/15">
              Back to pricing
            </Button>
          </div>
        )}
        {state.status === "timeout" && (
          <div data-testid="payment-timeout">
            <Loader2 className="w-14 h-14 mx-auto text-white/40" />
            <h1 className="font-display text-3xl mt-6">Still processing…</h1>
            <p className="text-white/55 mt-3 text-sm">
              Your payment may take a moment to settle. Check back on your dashboard shortly — we'll unlock everything as soon as it clears.
            </p>
            <Button onClick={() => navigate("/dashboard")}
              className="mt-6 rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
              Go to dashboard
            </Button>
          </div>
        )}
        {state.status === "error" && (
          <div>
            <XCircle className="w-16 h-16 mx-auto text-[#E52E71]" />
            <h1 className="font-display text-3xl mt-6">Something went wrong</h1>
            <p className="text-white/55 mt-3 text-sm">{state.message}</p>
            <Button onClick={() => navigate("/pricing")} className="mt-6 rounded-full bg-white/10">
              Back to pricing
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center" data-testid="payment-cancel">
        <div className="mx-auto w-16 h-16 rounded-full border border-white/10 flex items-center justify-center">
          <XCircle size={28} className="text-white/60" />
        </div>
        <h1 className="font-display text-3xl mt-6">Checkout cancelled</h1>
        <p className="text-white/55 mt-3 text-sm">No charge was made. You can come back whenever you're ready.</p>
        <div className="mt-6 flex justify-center gap-2">
          <Button onClick={() => navigate("/pricing")} className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
            See pricing
          </Button>
          <Button onClick={() => navigate("/dashboard")} variant="outline" className="rounded-full bg-transparent border-white/10">
            Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
